import { type FC, useEffect, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { Navigate, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { Button } from '../../../../components/Button/Button'
import { Spinner } from '../../../../components/Spinner/Spinner'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { addToast } from '../../../../store/toast/toastSlice'
import {
  useGetAccountSchedulingSettingsQuery,
  useGetSchedulingEventTypesQuery,
  useUpsertSchedulingEventTypeMutation,
} from '../../../../store/scheduling/scheduling'
import { EventTypeAccess, type SchedulingEventType } from '../../../../models/Scheduling'
import { StatusBanner } from '../../../Scheduling/shared/StatusBanner'
import { accountPageBanner } from '../../../Scheduling/shared/eventTypeHostState'
import { useCanEditAccountScheduling } from '../../../Scheduling/shared/useCanEditAccountScheduling'
import { accountBookingPagePath, eventTypeEditorPath, schedulingSetupPath } from '../../../Scheduling/shared/bookingNavigation'
import { SettingsCard } from '../sections/SettingsSection'
import { AccountPageEventTypeRow } from './AccountPageEventTypeRow'
import { AddEventTypesModal } from './AddEventTypesModal'

interface PageEntryState {
  openAdd?: boolean
}

const justAddedCopy = (count: number): string =>
  count === 1
    ? '1 event type added. Choose who takes its bookings to make it live.'
    : `${count} event types added. Choose who takes their bookings to make them live.`

const notLiveCopy = (notLiveCount: number, total: number): string =>
  `${notLiveCount} of ${total} event types on this page aren't live. Clients can't book them.`

export const AccountBookingPageView: FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const accountId = useAppSelector(selectSafeAuthenticatedUser).accountId
  const { data: settings, isLoading: isLoadingSettings, isError: isSettingsError } = useGetAccountSchedulingSettingsQuery({ accountId })
  const { data: eventTypes, isLoading: isLoadingEventTypes, isError: isEventTypesError } = useGetSchedulingEventTypesQuery({ accountId })
  const [upsertEventType, { isLoading: isSaving }] = useUpsertSchedulingEventTypeMutation()
  const isError = isSettingsError || isEventTypesError

  const canEdit = useCanEditAccountScheduling()
  const openAddOnEntry = Boolean((location.state as PageEntryState | null)?.openAdd)
  const [isAddOpen, setIsAddOpen] = useState(canEdit && openAddOnEntry)
  const [justAddedIds, setJustAddedIds] = useState<string[]>([])

  useEffect(() => {
    if (openAddOnEntry) {
      navigate(location.pathname, { replace: true, state: null })
    }
  }, [openAddOnEntry, navigate, location.pathname])

  useEffect(() => {
    if (isError) {
      dispatch(addToast({ variant: 'error', title: 'Could not load the account booking page', messages: ['Please try again.'] }))
    }
  }, [isError, dispatch])

  const accountBookingEnabled = Boolean(settings?.publicBookingPage.accountBookingEnabled)

  if (isError) {
    return <Navigate to={schedulingSetupPath} replace state={{ section: 'booking-page' }} />
  }
  if (isLoadingSettings || isLoadingEventTypes || !settings) {
    return (
      <div className='d-flex justify-content-center py-5'>
        <Spinner />
      </div>
    )
  }
  if (!accountBookingEnabled) {
    return <Navigate to={schedulingSetupPath} replace state={{ section: 'booking-page' }} />
  }

  const sharedEventTypes = (eventTypes ?? []).filter((eventType) => eventType.access === EventTypeAccess.ACCOUNT)
  const onPage = sharedEventTypes.filter((eventType) => Boolean(eventType.accountPageVisible))
  const candidates = sharedEventTypes.filter((eventType) => !eventType.accountPageVisible)
  const banner = justAddedIds.length > 0 ? null : accountPageBanner(onPage, accountBookingEnabled)

  const setOnPage = async (eventType: SchedulingEventType, accountPageVisible: boolean) =>
    upsertEventType({ accountId, eventType: { ...eventType, accountPageVisible } })

  const handleRemove = async (eventType: SchedulingEventType) => {
    const result = await setOnPage(eventType, false)
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not remove the event type', messages: ['Please try again.'] }))
      return
    }
    setJustAddedIds((current) => current.filter((id) => id !== eventType.id))
    dispatch(addToast({ variant: 'success', title: 'Removed from the account booking page', messages: [`${eventType.name} is no longer offered there.`] }))
  }

  const handleAdd = async (selected: SchedulingEventType[]) => {
    const results = await Promise.all(selected.map((eventType) => setOnPage(eventType, true)))
    const failedCount = results.filter((result) => 'error' in result).length
    const addedIds = selected.filter((_eventType, index) => !('error' in results[index])).map((eventType) => eventType.id)
    setIsAddOpen(false)
    setJustAddedIds(addedIds)
    if (failedCount > 0) {
      dispatch(
        addToast({
          variant: 'error',
          title: 'Could not add every event type',
          messages: [`${failedCount} of ${selected.length} could not be added. Please try again.`],
        }),
      )
    }
  }

  const openEditor = (eventType: SchedulingEventType) => {
    navigate(eventTypeEditorPath(eventType.id, 'booking-pages'), { state: { origin: accountBookingPagePath, mode: canEdit ? undefined : 'view' } })
  }

  const addButton = (
    <Button variant='primary' className='text-nowrap' onClick={() => setIsAddOpen(true)}>
      <i className='bi bi-plus-circle me-1' aria-hidden='true' />
      Add event types
    </Button>
  )

  return (
    <Container>
      <Row className='mb-4'>
        <Col className='d-flex align-items-center gap-2'>
          <Button
            variant='grayGhost'
            className='p-2'
            aria-label='Back to public booking pages'
            onClick={() => navigate(schedulingSetupPath, { state: { section: 'booking-page' } })}
          >
            <i className='bi bi-arrow-left' aria-hidden='true' />
          </Button>
        </Col>
      </Row>
      <div className='d-flex flex-column gap-3'>
        <nav aria-label='Breadcrumb' className='paragraph-medium zocks-text-gray-900'>
          <NavLink to={schedulingSetupPath} state={{ section: 'booking-page' }} className='zocks-text-gray-900 text-decoration-underline'>
            Public booking pages
          </NavLink>
          <span className='mx-1'>/</span>
          <span>Account booking page</span>
        </nav>
        <div className='d-flex align-items-start justify-content-between gap-3'>
          <div className='d-flex flex-column'>
            <h4 className='zocks-text-gray-900 mb-0'>Event types on the account booking page</h4>
            <div className='paragraph-small zocks-text-gray-600'>What clients can book, and who takes each booking.</div>
          </div>
          {canEdit && onPage.length > 0 ? addButton : null}
        </div>
        {justAddedIds.length > 0 ? <StatusBanner tone='info'>{justAddedCopy(justAddedIds.length)}</StatusBanner> : null}
        {banner?.kind === 'some-not-live' ? <StatusBanner tone='warning'>{notLiveCopy(banner.notLiveCount, banner.total)}</StatusBanner> : null}
        {banner?.kind === 'none-live' ? (
          <StatusBanner tone='danger'>No event types are live on this page. Clients can&apos;t book anything here.</StatusBanner>
        ) : null}
        {onPage.length === 0 ? (
          <div className='d-flex flex-column align-items-center gap-3 py-5 border-top border-bottom zocks-border-color-gray-200 text-center'>
            <div className='d-flex flex-column zocks-gap-0.25'>
              <span className='bold'>No event types on this page yet</span>
              <span className='paragraph-small zocks-text-gray-600'>Clients can&apos;t book anything here.</span>
            </div>
            {canEdit ? addButton : null}
          </div>
        ) : (
          <SettingsCard>
            {onPage.map((eventType) => (
              <AccountPageEventTypeRow
                key={eventType.id}
                eventType={eventType}
                canEdit={canEdit}
                quiet={justAddedIds.includes(eventType.id)}
                onEditHosts={() => openEditor(eventType)}
                onRemove={() => void handleRemove(eventType)}
              />
            ))}
          </SettingsCard>
        )}
      </div>
      {isAddOpen ? (
        <AddEventTypesModal candidates={candidates} isAdding={isSaving} onAdd={(selected) => void handleAdd(selected)} onCancel={() => setIsAddOpen(false)} />
      ) : null}
    </Container>
  )
}
